import { extendTheme } from 'native-base';

export const theme = extendTheme({
  fontConfig: {
    Poppins: {
      100: {
        normal: 'Poppins-Thin',
        italic: 'Poppins-ThinItalic',
      },
      200: {
        normal: 'Poppins-ExtraLight',
        italic: 'Poppins-ExtraLightItalic',
      },
      300: {
        normal: 'Poppins-Light',
        italic: 'Poppins-LightItalic',
      },
      400: {
        normal: 'Poppins-Regular',
        italic: 'Poppins-Italic',
      },
      500: {
        normal: 'Poppins-Medium',
        italic: 'Poppins-MediumItalic',
      },
      600: {
        normal: 'Poppins-SemiBold',
        italic: 'Poppins-SemiBoldItalic',
      },
      700: {
        normal: 'Poppins-Bold',
        italic: 'Poppins-BoldItalic',
      },
      800: {
        normal: 'Poppins-ExtraBold',
        italic: 'Poppins-ExtraBoldItalic',
      },
      900: {
        normal: 'Poppins-Black',
        italic: 'Poppins-BlackItalic',
      },
    },
  },

  fonts: {
    heading: 'Poppins',
    body: 'Poppins',
    mono: 'Poppins',
  },

  colors: {
    primary: {
      100: '#02151f',
      200: '#1cad98',
      300: '#26dbc0',
      400: '#2fefd2',
    },
    green: {
      200: '#bfff5b',
    },
    blue: {
      100: '#0a222e',
      200: '#70d0ff',
      300: '#8cd9ff',
    },
    red: {
      100: '#2d131a',
      200: '#ff3870',
    },
    orange: {
      100: '#6d4d26ff',
      200: '#ffb358',
      300: '#ffbe6e',
    },
    yellow: {
      100: '#262014',
      200: '#ffd545',
      300: '#ffde6b',
    },
    purple: {
      100: '#2b1638',
      200: '#bd9bf7',
      300: '#d0b6fc',
    },
    pink: {
      100: '#260913',
      200: '#ff6497',
      300: '#ff82ac',
    },

    // Surfaces/Modals
    surface: {
      100: '#09011D',
      150: '#120A27',
      200: '#1e1e38',
      300: '#25253f',
      400: '#393961',
      500: '#5a5a8c',
      600: '#8f8fc7',
      700: '#b9b9df',
    },

    // Feedback Implementation States
    feedback: {
      reviewed: '#ffbe6e',
      inDevelopment: '#bfff5b',
      live: '#2fefd2',
      backlog: '#ff6497',
      default: '#5a5a8c',
      bug: '#ff3870',
    },

    // Default user colors
    userDefaultColors: {
      theme: '#ffd545',
    },

    // Dark
    dark: {
      background: '#0B0029',
      textContrast: '#FFFFFF',
      darkBorder: '#25253f',
      loadingDark: '#4D5259',
    },
    light: {
      background: '#FFFFFF',
      textContrast: '#0B0029',
    },
  },

  components: {
    Skeleton: {
      variants: {
        dark: ({ colorScheme }: any) => {
          return {
            startColor: theme.colors.surface[100],
            endColor: theme.colors.surface[200],
          };
        },
      },
    },
    Button: {
      variants: {
        primary: ({ colorScheme }: any) => {
          return {
            px: '5',
            py: '4',
            size: 'md',
            rounded: 'full',
            _text: {
              textAlign: 'center',
              color: 'dark.textContrast',
              fontSize: 'lg',
              fontFamily: 'body',
              fontWeight: 600,
              fontStyle: 'normal',
            },
          };
        },
        secondary: ({ colorScheme }: any) => {
          return {
            px: '5',
            py: '4',
            size: 'md',
            _text: {
              textAlign: 'center',
              color: 'dark.textContrast',
              fontSize: 'lg',
              fontFamily: 'body',
              fontWeight: 600,
              fontStyle: 'normal',
            },
            style: { backgroundColor: 'transparent' },
          };
        },
      },
    },
    Heading: {
      baseStyle: {
        fontFamily: 'heading',
        fontSize: 36,
        lineHeight: 36,
        fontWeight: 700,
        letterSpacing: '-0.5px',
        // paddingTop: 1,
      },
    },
    Text: {
      baseStyle: {
        fontFamily: 'body',
        fontSize: 16,
        lineHeight: 22,
        fontWeight: 400,
        letterSpacing: '-0.3px',
        // paddingTop: 1, // -> for Noir_Pro
      },
      variants: {
        error: ({ colorScheme }: any) => {
          return {
            style: {
              color: `${theme.colors.red[200]}`,
            },
            _text: {
              fontFamily: 'body',
              fontSize: 14,
              fontWeight: 500,
            },
          };
        },
        semibold: ({ colorScheme }: any) => {
          return {
            fontFamily: 'body',
            fontSize: 20,
            fontWeight: 600,
          };
        },
        usernameProfile: ({ colorScheme }: any) => {
          return {
            fontFamily: 'heading',
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: '-0.3px',
          };
        },
        chatTitle: ({ colorScheme }: any) => {
          return {
            fontFamily: 'heading',
            fontSize: 16,
            lineHeight: 20,
            fontWeight: 700,
            letterSpacing: '-0.3px',
            paddingTop: 1,
          };
        },

        chatMessage: ({ colorScheme }: any) => {
          return {
            fontFamily: 'body',
            fontSize: 16,
            fontWeight: 200,
          };
        },
        header: ({ colorScheme }: any) => {
          return {
            fontFamily: 'body',
            fontSize: 27,
            fontWeight: 600,
            letterSpacing: '-0.7px',
            // paddingTop: 1, // -> for Noir_Pro
          };
        },
        headerMini: ({ colorScheme }: any) => {
          return {
            fontFamily: 'heading',
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: '-0.5px',
            paddingTop: 1,
          };
        },
      },
    },
    Badge: {
      baseStyle: {
        _text: {
          fontFamily: 'body',
          fontWeight: 600,
          fontSize: 14,
          lineHeight: 16,
        },
      },
    },
    VStack: {
      variants: {
        modal: ({ colorScheme }: any) => {
          return {
            bg: 'dark.background',
            px: '10',
            pb: '5',
            pt: '10',
            rounded: '3xl',
            space: '5',
          };
        },
      },
    },
    Checkbox: {
      variants: {
        toggle: ({ colorScheme }: any) => {
          return {
            bg: `${colorScheme}.500`,
            rounded: 'full',
          };
        },
      },
    },
    Alert: {
      variants: {
        'left-accent': ({ colorScheme }: any) => {
          return {
            bg: `${colorScheme.subtle}`,
            border: '1px solid red',
            color: 'red',
          };
        },
      },
    },
  },

  config: {
    initialColorMode: 'dark',
  },
});
